import {
  Button,
  ClickAwayListener,
  Grow,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {Fragment, forwardRef, useEffect, useRef, useState} from 'react';

/**
 * A button component for the NavBar menu
 */
export const NavButton = forwardRef(
  ({title, children, disabled, ...props}, ref) => {
    return (
      <Tooltip title={title || ''} placement="bottom">
        <span>
          <Button
            sx={{
              width: 34,
              height: 34,
              minWidth: 34,
              '&.Mui-disabled': {
                pointerEvents: 'auto',
              },
            }}
            color="inherit"
            size="small"
            ref={ref}
            disabled={disabled}
            {...props}
          >
            {children}
          </Button>
        </span>
      </Tooltip>
    );
  },
);

NavButton.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  children: PropTypes.node,
  disabled: PropTypes.bool,
};

const NavMenuButton = ({label, title, children, ...props}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef();

  const handleClick = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleListKeyDown = event => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const menuItems = React.Children.toArray(children).map(child =>
    React.cloneElement(child, {
      onClick: event => {
        handleClose(event);
        if (child.props.onClick) {
          child.props.onClick();
        }
      },
    }),
  );

  return (
    <Fragment>
      <NavButton ref={anchorRef} onClick={handleClick} title={title} {...props}>
        {label}
      </NavButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        disablePortal
        sx={{
          zIndex: theme => theme.zIndex.modal + 1,
        }}
      >
        {({TransitionProps}) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'top right',
            }}
          >
            <Paper elevation={10}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} onKeyDown={handleListKeyDown}>
                  {menuItems}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Fragment>
  );
};

NavMenuButton.propTypes = {
  label: PropTypes.node,
  children: PropTypes.node,
  menuProps: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default NavMenuButton;
